import {
    findDeliveryServiceByDeliveryNo,
    findDeliveryServiceByServiceId,
} from '@repo/api-adapters/services/services.api.utils';
import type { OrderAsset } from '@repo/types/order';
import { type ModifyDeliveryTimeWindowCommand, OrderUpdateCommandType } from '@repo/types/order-update';
import type { TimeWindowResponseItem } from '@repo/types/time-windows';
import { invariant } from '@repo/utils/type.utils';

interface BuildModifyDeliveryTimeWindowCommandParams {
    orderData: OrderAsset;
    serviceId: string;
    timeWindow: TimeWindowResponseItem;
    reasonCode: string;
    deliveryNo?: string | undefined;
}

/**
 * Builds a delivery time-window command from the selected window and matching delivery service.
 */
export const buildModifyDeliveryTimeWindowCommand = ({
    orderData,
    serviceId,
    timeWindow,
    reasonCode,
    deliveryNo,
}: BuildModifyDeliveryTimeWindowCommandParams): ModifyDeliveryTimeWindowCommand => {
    const matchedDeliveryService = deliveryNo
        ? findDeliveryServiceByDeliveryNo(orderData, deliveryNo)
        : findDeliveryServiceByServiceId(orderData, serviceId);
    const resolvedDeliveryNo = matchedDeliveryService?.deliveryNo;

    invariant(resolvedDeliveryNo, `Delivery service not found for serviceId: ${serviceId}`);

    return {
        type: OrderUpdateCommandType.ModifyDeliveryTimeWindow,
        id: crypto.randomUUID(),
        deliveryNo: Number(resolvedDeliveryNo),
        deliveryTimeWindow: {
            deliveryDateTimeFrom: timeWindow.fromDateTimeUtc,
            deliveryDateTimeTo: timeWindow.toDateTimeUtc,
            timeZoneId: timeWindow.timeZone,
            resourcePoolKey: timeWindow.resourcePoolId ?? '',
            capacityAvailable: timeWindow.capacityAvailable ?? true,
            timeWindowId: timeWindow.timeWindowId,
            paymentCutOffDateTime: timeWindow.paymentCutOffDateTimeUtc ?? '',
            hardPaymentCutOffDateTime: timeWindow.paymentCutOffDateTimeUtc ?? '',
            transferringBusinessUnit: {
                buCode: timeWindow.transferringUnit?.code ?? '',
                buType: timeWindow.transferringUnit?.type ?? '',
            },
        },
        tspName: timeWindow.tspName ?? '',
        tspNumber: timeWindow.tspID ?? '',
        reasonCode,
    };
};
